import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { ListBulletIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Button } from '../components_catalyst/button';
import { Input, InputGroup } from '../components_catalyst/input';
import { ViewMode } from '../utils/mapListingUtils';

interface MapListToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

/**
 * Toolbar component for map listing pages with search input and view toggle.
 * Provides Google Drive-like UX for filtering and switching between list/card views.
 */
export function MapListToolbar({
  searchTerm,
  onSearchChange,
  viewMode,
  onViewModeChange,
}: MapListToolbarProps) {
  return (
    <div className="flex items-center gap-4">
      {/* Search Input */}
      <div className="w-64">
        <InputGroup>
          <MagnifyingGlassIcon data-slot="icon" />
          <Input
            type="search"
            placeholder="Search maps..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search maps"
          />
        </InputGroup>
      </div>

      {/* View Toggle */}
      <div
        className="flex items-center rounded-lg border border-zinc-950/10 dark:border-white/10"
        role="group"
        aria-label="View options"
      >
        <Button
          plain
          onClick={() => onViewModeChange('list')}
          aria-pressed={viewMode === 'list'}
          className={clsx(
            'rounded-r-none border-r border-zinc-950/10 dark:border-white/10',
            viewMode === 'list' && 'bg-zinc-100 dark:bg-zinc-800'
          )}
        >
          <ListBulletIcon className="size-5" aria-hidden="true" />
          <span className="sr-only">List view</span>
        </Button>
        <Button
          plain
          onClick={() => onViewModeChange('card')}
          aria-pressed={viewMode === 'card'}
          className={clsx(
            'rounded-l-none',
            viewMode === 'card' && 'bg-zinc-100 dark:bg-zinc-800'
          )}
        >
          <Squares2X2Icon className="size-5" aria-hidden="true" />
          <span className="sr-only">Card view</span>
        </Button>
      </div>
    </div>
  );
}
